import {
  OmniElement,
  OmniStyleElement,
  html,
  css,
  nothing,
  ref,
  createRef,
  classMap,
} from 'omni-ui';

// eslint-disable-next-line import/no-extraneous-dependencies
import Swiper, { Navigation, Pagination } from 'swiper';
import './campaign-wysiwyg.js';

// Hardcoded styles from swiper since they can't be linked. Refer to file for reason.
import { swiperStyles } from '../third-party/swiper.styles.js';

// configure Swiper to use imported modules
Swiper.use([Navigation, Pagination]);

OmniStyleElement.register();

/**
 * @summary Class for Swiper Image Carousel
 * @description dumb component that takes in an array of images and displays them in a carousel
 * @extends OmniElement
 */
export class CampaignImageCarousel extends OmniElement {
  /**
   * ImageCarousel Lit Properties
   * @property {Function} handleToggle - Function that gets passed down that handles toggling of the Boolean showCarousel and also passes down the imageIndex the carousel needs to open on
   * @property {Array} images - array of objects containing the image url and title
   * @property {Number} imageIndex - Index number of the image the carousel needs to open on
   * @property {Boolean} showCarousel - flag that keeps track if the carousel is open or not
   */
  static get properties() {
    return {
      handleToggle: { type: Function },
      images: { type: Array },
      imageIndex: { type: Number },
      showCarousel: { type: Boolean },
    };
  }

  /**
   * Image carousel styles, uses imported swiperStyles
   */
  static get styles() {
    return [
      super.styles,
      swiperStyles,
      css`
        .carousel-container {
          display: grid;
          grid-template-rows: 1fr 6fr;
          height: 100vh;
        }

        .no-description {
          grid-template-rows: 1fr;
          margin-top: 3rem;
        }

        .carousel-description {
          display: flex;
          flex-flow: column nowrap;
          color: rgb(var(--rgb-core-light));
          gap: 1.429rem;
          z-index: 1;
          margin: 3rem 9rem 0 9rem;
        }

        .carousel-title {
          font-size: 1.286rem;
          font-weight: 600;
          line-height: 1.33;
          letter-spacing: 0.029rem;
        }

        .carousel-content {
          overflow-y: auto;
          max-height: 16.7vh;
        }

        .carousel-slides {
          min-height: 0;
          display: flex;
          margin: 3rem;
        }

        .swiper {
          height: 100%;
          width: 95vw;
          display: flex;
          place-content: center;
          align-items: center;
          flex-flow: column nowrap;
        }
        .swiper-wrapper {
          min-height: 0;
          flex: 1;
        }
        .swiper-slide img {
          width: 100%;
          height: 100%;
          object-fit: scale-down;
        }
        .omni .img-title {
          text-align: center;
          color: rgb(var(--rgb-core-light));
          margin-top: 2.29rem;
          line-height: 0;
        }
        .navigation-container {
          display: flex;
          align-items: center;
          margin-top: 5.14rem;
        }
        .swiper-button-prev,
        .swiper-button-next {
          background-color: rgb(var(--rgb-primary));
          border-radius: 50%;
          height: 2.29rem;
          width: 2.29rem;
          flex-shrink: 0;
        }
        .swiper-pagination {
          text-align: right;
          padding-right: 1rem;
          padding-left: 1rem;
          position: static;
          color: rgb(var(--rgb-core-light));
          bottom: 0.14rem;
        }
        .swiper-button-prev,
        .swiper-button-next,
        .swiper-button-prev:after,
        .swiper-button-next:after,
        .swiper-rtl {
          content: none;
          color: rgb(var(--rgb-core-light));
          position: static;
        }
        .omni omni-icon {
          --color-icon-lines: rgb(var(--rgb-core-light));
          fill: rgb(var(--rgb-core-light));
        }
        .omni .modal-close {
          background: none;
          z-index: 1;
        }
      `,
    ];
  }

  constructor() {
    super();
    this.carouselDescription = '';
    this.paginationRef = createRef();
    this.prevBtnRef = createRef();
    this.nextBtnRef = createRef();
    this.swiperRef = createRef();
  }

  /**
   * @description initializes a new Swiper carousel along with the configuration of the carousel, pagination, and buttons.
   */
  firstUpdated() {
    this.swiper = new Swiper(this.swiperRef.value, {
      slidesPerView: 1,
      pagination: {
        el: this.paginationRef.value,
        type: 'fraction',
      },
      navigation: {
        prevEl: this.prevBtnRef.value,
        nextEl: this.nextBtnRef.value,
      },
    });
  }

  /**
   * @description utilizes Swiper method slideTo to open carousel on the image selected by user using {@link #imageIndex}
   */
  updated() {
    if (this.imageIndex !== undefined) {
      this.swiper?.slideTo(this.imageIndex, 0, false);
    }
  }

  /**
   *
   * @returns main html template for {@link #CampaignImageCarousel}. Utilizes a map function on {@link #images} to create the html for each individual image.
   */
  render() {
    return html` <omni-style>
      <div class="modal ${this.showCarousel ? 'is-active' : ''}">
        <div
          aria-hidden="true"
          class="modal-background"
          @click="${() => this.handleToggle()}"
        ></div>

        <div
          class="carousel-container ${classMap({
            'no-description': !this.carouselDescription,
          })}"
        >
          ${this.carouselDescription
            ? html`
                <div class="carousel-description">
                  <p class="carousel-title">
                    ${this.carouselDescription.title}
                  </p>
                  <div class="carousel-content">
                    <campaign-wysiwyg
                      .initialContent=${this.carouselDescription.content}
                      display-only
                      overflow-visible
                    ></campaign-wysiwyg>
                  </div>
                </div>
              `
            : nothing}
          <div class="carousel-slides">
            <div class="swiper" ${ref(this.swiperRef)}>
              <div class="swiper-wrapper">
                ${this.images.map(
                  image => html`
                    <div class="swiper-slide">
                      <img src="${image.url}" alt="" />
                      <p class="img-title">${image.title}</p>
                    </div>
                  `,
                )}
              </div>
              <div class="navigation-container">
                <div class="swiper-button-prev" ${ref(this.prevBtnRef)}>
                  <omni-icon
                    class="is-size-1"
                    icon-id="omni:interactive:back"
                  ></omni-icon>
                </div>
                <div class="swiper-pagination" ${ref(this.paginationRef)}></div>
                <div class="swiper-button-next" ${ref(this.nextBtnRef)}>
                  <omni-icon
                    class="is-size-1"
                    icon-id="omni:interactive:forward"
                  ></omni-icon>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          class="modal-close is-large"
          aria-label="close"
          @click="${() => this.handleToggle()}"
        ></button>
      </div>
    </omni-style>`;
  }
}

OmniElement.register('campaign-image-carousel', CampaignImageCarousel);
